<template>
    <div class="ordering-time-input" :class="{ 'is-invalid': invalid }" @pointerdown.capture="prepareSelection" @keydown.capture="onKeydown">
        <ElConfigProvider :locale="locale">
            <ElTimePicker ref="picker" :id="id" :model-value="draft" format="HH:mm" value-format="HH:mm"
                :aria-label="'Heure de ' + label.toLowerCase()" placeholder="HH:mm" :disabled="disabled"
                :editable="false" :clearable="false" :save-on-blur="false" :automatic-dropdown="false"
                :default-value="defaultTime" :placement="alignEnd ? 'bottom-end' : 'bottom-start'"
                :popper-class="'ordering-time-picker-popover ' + id + '-popover'" :popper-options="popperOptions"
                @update:model-value="updateDraft" @change="commitSelection" @visible-change="onVisibleChange" />
        </ElConfigProvider>
        <feather type="chevron-down" size="16" class="time-chevron" aria-hidden="true" />
    </div>
</template>

<script>
import { ElTimePicker, CommonPicker } from "element-plus/es/components/time-picker/index.mjs";
import { ElConfigProvider } from "element-plus/es/components/config-provider/index.mjs";
import { ElInput } from "element-plus/es/components/input/index.mjs";
import fr from "element-plus/es/locale/lang/fr.mjs";
import "element-plus/es/components/time-picker/style/css.mjs";

// Keep Vue 3 model events intact inside the application's Vue 2 compatibility runtime.
for (const component of [ElTimePicker, CommonPicker, ElInput]) {
    component.compatConfig = { MODE: 3 };
}

export default {
    components: { ElTimePicker, ElConfigProvider },
    props: {
        id: { type: String, required: true },
        label: { type: String, required: true },
        value: { type: String, required: true },
        disabled: { type: Boolean, default: false },
        invalid: { type: Boolean, default: false },
        describedBy: { type: String, default: undefined },
        alignEnd: { type: Boolean, default: false },
    },
    emits: ["input"],
    data() {
        return {
            draft: this.value || null,
            open: false,
            cancelled: false,
            defaultTime: new Date(2000, 0, 1, 12, 0),
            locale: { ...fr, el: { ...fr.el, datepicker: { ...fr.el.datepicker, confirm: "Valider" } } },
            popperOptions: {
                modifiers: [
                    { name: "preventOverflow", options: { padding: 12 } },
                    { name: "flip", options: { padding: 12 } },
                ],
            },
        };
    },
    watch: {
        value(value) { this.draft = value || null; },
        disabled(value) { if (value) this.cancelSelection(); },
    },
    mounted() { this.syncInputAccessibility(); },
    updated() { this.syncInputAccessibility(); },
    beforeUnmount() { this.removeListeners(); },
    methods: {
        prepareSelection() {
            if (!this.disabled && this.$el.querySelector("input")?.getAttribute("aria-expanded") !== "true") this.cancelled = false;
        },
        updateDraft(value) {
            if (!this.disabled && !this.cancelled) this.draft = value;
        },
        commitSelection(value) {
            if (this.disabled || this.cancelled || typeof value !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return;
            if (value !== this.value) this.$emit("input", value);
        },
        cancelSelection() {
            this.cancelled = true;
            this.draft = this.value || null;
            this.$refs.picker?.handleClose();
        },
        onVisibleChange(open) {
            this.open = open;
            if (open) {
                this.cancelled = false;
                document.addEventListener("pointerdown", this.onOutsidePointer, true);
                document.addEventListener("keydown", this.onKeydown, true);
            } else {
                this.removeListeners();
                this.draft = this.value || null;
                if (!this.disabled && document.activeElement === document.body) this.$refs.picker?.focus();
            }
        },
        removeListeners() {
            document.removeEventListener("pointerdown", this.onOutsidePointer, true);
            document.removeEventListener("keydown", this.onKeydown, true);
        },
        onOutsidePointer(event) {
            const popover = document.getElementsByClassName(this.id + "-popover")[0];
            if (!this.$el.contains(event.target) && !popover?.contains(event.target)) this.cancelSelection();
        },
        onKeydown(event) {
            if (["Enter", "ArrowDown"].includes(event.key)) this.prepareSelection();
            // The native state changes before the popover's opening transition finishes.
            const expanded = this.$el.querySelector("input")?.getAttribute("aria-expanded") === "true";
            if (event.key !== "Escape" || (!this.open && !expanded)) return;
            event.preventDefault();
            event.stopPropagation();
            this.cancelSelection();
            this.$refs.picker?.focus();
        },
        syncInputAccessibility() {
            // Element Plus forwards these attributes to its popover rather than the native input.
            const input = this.$el.querySelector("input");
            if (!input) return;
            if (this.invalid) input.setAttribute("aria-invalid", "true");
            else input.removeAttribute("aria-invalid");
            if (this.describedBy) input.setAttribute("aria-describedby", this.describedBy);
            else input.removeAttribute("aria-describedby");
        },
    },
};
</script>

<style scoped>
.ordering-time-input { position: relative; min-width: 0; --el-color-primary: var(--main-color-light); }
.ordering-time-input :deep(.el-date-editor.el-input) { width: 100%; height: 46px; }
.ordering-time-input :deep(.el-input__wrapper) { padding: 0 .75rem; border: 1px solid #dce5e9; border-radius: 12px; background: white; box-shadow: none; cursor: pointer; }
.ordering-time-input :deep(.el-input__wrapper:hover) { border-color: #9ecdd2; }
.ordering-time-input :deep(.el-input__wrapper.is-focus) { border-color: var(--main-color-light); box-shadow: 0 0 0 3px color-mix(in srgb, var(--main-color-light) 18%, transparent); }
.ordering-time-input :deep(.el-input__inner) { height: 44px; padding-right: 1.25rem; border: 0 !important; border-radius: 0 !important; background: transparent; color: #083e60; font: inherit; font-weight: 600; font-variant-numeric: tabular-nums; cursor: pointer; }
.ordering-time-input :deep(.el-input__inner:focus) { box-shadow: none; outline: none; }
.ordering-time-input :deep(.el-input__prefix) { color: var(--main-color-light); }
.ordering-time-input :deep(.el-input__icon) { font-size: 17px; }
.ordering-time-input.is-invalid :deep(.el-input__wrapper) { border-color: #b52f40; }
.ordering-time-input :deep(.el-input.is-disabled .el-input__wrapper) { background: #f8f9fa; cursor: not-allowed; }
.ordering-time-input :deep(.el-input.is-disabled .el-input__inner) { color: #73828c; -webkit-text-fill-color: #73828c; cursor: not-allowed; }
.time-chevron { position: absolute; right: .75rem; top: 15px; color: #567382; pointer-events: none; }
</style>

<style>
.ordering-time-picker-popover.el-popper {
    --el-color-primary: var(--main-color-light);
    --el-text-color-primary: #083e60;
    --el-text-color-regular: #567382;
    --el-border-color-light: #dce5e9;
    border: 1px solid #dce5e9;
    border-radius: 16px;
    box-shadow: 0 12px 32px rgb(8 62 96 / 14%);
    font-family: inherit;
}
.ordering-time-picker-popover .el-time-panel { width: 260px; max-width: calc(100vw - 24px); padding-top: 40px; border-radius: 16px; }
.ordering-time-picker-popover .el-time-panel::before,
.ordering-time-picker-popover .el-time-panel::after { position: absolute; top: 13px; transform: translateX(-50%); color: #567382; font-size: 12px; font-weight: 600; }
.ordering-time-picker-popover .el-time-panel::before { content: "Heures"; left: 25%; }
.ordering-time-picker-popover .el-time-panel::after { content: "Minutes"; left: 75%; }
.ordering-time-picker-popover .el-time-spinner__wrapper { max-height: 220px; }
.ordering-time-picker-popover .el-time-spinner__list::before,
.ordering-time-picker-popover .el-time-spinner__list::after { height: 88px; }
.ordering-time-picker-popover .el-time-spinner__item { height: 44px; line-height: 44px; margin: 0 10px; border-radius: 10px; color: #567382; font-size: 16px; font-variant-numeric: tabular-nums; }
.ordering-time-picker-popover .el-time-spinner__item:hover:not(.is-disabled):not(.is-active) { background: #f0f8f9; }
.ordering-time-picker-popover .el-time-spinner__item.is-active:not(.is-disabled) { background: #e5f5f6; color: #135767; font-weight: 700; box-shadow: inset 0 0 0 1px #9ecdd2; }
.ordering-time-picker-popover .el-time-panel__content::before,
.ordering-time-picker-popover .el-time-panel__content::after { display: none; }
.ordering-time-picker-popover .el-scrollbar__wrap { overscroll-behavior: contain; }
.ordering-time-picker-popover .el-time-panel__footer { display: flex; gap: 8px; justify-content: flex-end; height: auto; padding: 12px; border-color: #edf1f3; }
.ordering-time-picker-popover .el-time-panel__btn { min-width: 94px; min-height: 44px; margin: 0; padding: 0 16px; border-radius: 22px; font: inherit; font-size: 14px; font-weight: 600; }
.ordering-time-picker-popover .el-time-panel__btn.cancel { color: #567382; }
.ordering-time-picker-popover .el-time-panel__btn.cancel:hover { background: #f0f8f9; }
.ordering-time-picker-popover .el-time-panel__btn.confirm { background: var(--main-color-light); color: white; }
.ordering-time-picker-popover .el-time-panel__btn.confirm:hover { background: color-mix(in srgb, var(--main-color-light) 86%, #083e60); }
.ordering-time-picker-popover .el-time-panel__btn:focus-visible { outline: 2px solid #135767; outline-offset: 2px; }
</style>
